import { useEffect, useState } from 'react'
import { toPageMeta } from './to-page-meta'
import { sdkErrorMessage, useAuthServiceClient } from './sdk-client'

type Client = Awaited<ReturnType<ReturnType<typeof useAuthServiceClient>>>
type Params = NonNullable<Parameters<Client['auth']['getAccountChangeSets']>[1]>
type Row = NonNullable<Awaited<ReturnType<Client['auth']['getAccountChangeSets']>>['data']>[number]

export type ChangeSetRow = Row

export interface ChangeSetFilters {
  page: number
  perPage: number
  resourceType?: NonNullable<Params['resourceType_']>
  permissionSetKey?: string
  since?: string
}

export interface ChangeSetsMeta {
  page: number
  perPage: number
  totalCount: number
  totalPages: number
}

export type ChangeSetsState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; pending: boolean; data: ChangeSetRow[]; meta: ChangeSetsMeta }

// The SDK names the bracketed resource_type[] param `resourceType_`. State is tagged with the
// request it answers: another slug's rows never show (loading); a filter change keeps the rows
// with pending: true. The effect's `cancelled` flag makes the latest request win.
export function useChangeSets(slug: string, filters: ChangeSetFilters): ChangeSetsState {
  const getClient = useAuthServiceClient()
  // Callers build `filters` inline, so depend on its value, not its identity.
  const filtersKey = JSON.stringify(filters)
  const [tagged, setTagged] = useState<{ slug?: string; filtersKey?: string; state: ChangeSetsState }>({
    state: { status: 'loading' },
  })

  useEffect(() => {
    let cancelled = false
    const f = JSON.parse(filtersKey) as ChangeSetFilters
    const params: Params = {
      page: f.page,
      perPage: f.perPage,
      resourceType_: f.resourceType?.length ? f.resourceType : undefined,
      permissionSetKey: f.permissionSetKey,
      since: f.since,
    }
    const settle = (state: ChangeSetsState) => {
      if (!cancelled) setTagged({ slug, filtersKey, state })
    }

    getClient()
      .then((client) => client.auth.getAccountChangeSets(slug, params))
      .then((response) => {
        const data = response.data ?? []
        settle({
          status: 'ready',
          pending: false,
          data,
          meta: toPageMeta(response.meta, f.page, f.perPage, data.length),
        })
      })
      .catch((error: unknown) => settle({ status: 'error', message: sdkErrorMessage(error, 'Failed to load history.') }))

    return () => {
      cancelled = true
    }
  }, [getClient, slug, filtersKey])

  if (tagged.slug === slug && tagged.filtersKey === filtersKey) return tagged.state
  if (tagged.slug === slug && tagged.state.status === 'ready') return { ...tagged.state, pending: true }
  return { status: 'loading' }
}
